import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import { submitReport, REPORT_REASONS } from "@/lib/reports";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

/**
 * ReportButton — small inline button + modal usable on event/profile pages.
 * Props: targetType ('event' | 'profile' | 'message'), targetId, label?
 */
export default function ReportButton({ targetType, targetId, label = "Report", testid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onOpen = () => {
    if (!user) { navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitReport({ reporterId: user.id, targetType, targetId, reason, details });
      toast.success("Reported. Thanks — we'll look at it.");
      setOpen(false);
      setDetails("");
      setReason("spam");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        data-testid={testid || `report-${targetType}-btn`}
        className="inline-flex items-center gap-1.5 text-xs text-ink/55 hover:text-destructive transition-colors"
      >
        <Flag size={12} strokeWidth={2} /> {label}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="report-modal"
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink/40 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="pop bg-paper p-0 w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-2">
              <h3 className="font-display text-2xl tracking-tight">Report {targetType}</h3>
              <button onClick={() => setOpen(false)} aria-label="Close"
                className="w-9 h-9 inline-flex items-center justify-center border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime transition-colors">
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="px-5 pb-5 flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-ink/65">Reason</span>
                <select value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="report-reason"
                  className="chunky-input">
                  {REPORT_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-ink/65">Details (optional)</span>
                <textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500}
                  data-testid="report-details"
                  placeholder="Anything specific we should know?" className="chunky-input" />
              </label>
              <button type="submit" disabled={submitting} data-testid="report-submit"
                className="cta-primary w-full">
                {submitting ? "Sending…" : "Send report"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
